import { useCallback, useRef } from 'react'
import { setPopup } from '../redux/slices/Map/map'
import { detectCityFromURL } from '../utils/cityDetector'
import { buildCantonUrl, buildTreePublicUrl } from '../utils/cantonUrl'
import * as RT from '../config/routeType'

export const useTreeSelection = ({
  map,
  dispatch,
  navigate,
  pathname,
  cantones,
  setPropsContent,
  setPopupLngLat,
  popupLoading = false,
}) => {
  const isSelectingRef = useRef(false)
  // Ref para leer el valor más reciente de popupLoading sin recrear selectTree
  // (si popupLoading estuviera en deps de useCallback, useConfigureMapLayers re-registraría
  // todos los handlers de click del mapa en cada cambio de estado de carga)
  const popupLoadingRef = useRef(popupLoading)
  popupLoadingRef.current = popupLoading

  const selectTree = useCallback(
    (feature, coordinates, source = 'click') => {
      const isNavigationSelection = source === 'navigation'
      if (isSelectingRef.current || (popupLoadingRef.current && !isNavigationSelection)) {
        return false
      }
      isSelectingRef.current = true

      try {
        const treeId = feature.properties.id
        const code = feature.properties.code

        // Normalizar coordenadas (antimeridiano)
        const coords = [...coordinates]
        if (map) {
          const centerLng = map.getCenter?.()?.lng ?? 0
          while (Math.abs(centerLng - coords[0]) > 180) {
            coords[0] += centerLng > coords[0] ? 360 : -360
          }
        }

        // 1. Actualizar contenido del popup
        setPropsContent({ tree: feature })
        setPopupLngLat(coords)

        // 2. Abrir popup en Redux (CRÍTICO)
        dispatch(
          setPopup({
            open: true,
            code: code,
            id: treeId,
          })
        )

        // 3. Actualizar UI
        dispatch({ type: 'drawerLeft/hide' })
        dispatch({ type: 'modal/disableButton' })
        dispatch({ type: 'drawerRight/show', payload: false })

        // 4. Navegar
        const currentCity = detectCityFromURL(pathname) || 'loja'

        if (pathname.includes(RT.MANAGEMENT_TREE)) {
          // En modo edición: no navegar automáticamente al árbol clickeado.
          // El popup ya está abierto; el usuario decide con el botón del popup.
          return true
        }

        let targetRoute = buildTreePublicUrl(currentCity, cantones, code)
        if (pathname.includes(RT.REGISTER_TREE)) {
          targetRoute = buildCantonUrl(currentCity, cantones, `/${RT.REGISTER_TREE}`)
        }

        navigate(targetRoute, { replace: true })

        // 5. El vuelo al árbol es manejado reactivamente en MapboxGL/index.jsx
        //    al detectar el cambio en popupLngLat.
        return true
      } catch (error) {
        console.error('[selectTree] ❌ Error:', error)
        return false
      } finally {
        setTimeout(() => {
          isSelectingRef.current = false
        }, 100)
      }
    },
    [map, dispatch, navigate, pathname, cantones, setPropsContent, setPopupLngLat]
  )

  return { selectTree }
}
